import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../App';
import { Sparkles, CheckCircle2, Zap, TrendingUp, Crown, Loader2, Receipt, AlertCircle, Gift, Shield } from 'lucide-react';
import { toast } from 'sonner';

const AICreditsPage = () => {
  const { api, user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [balance, setBalance] = useState(0);
  const [packages, setPackages] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [payingPp, setPayingPp] = useState(null);
  const [capturingPp, setCapturingPp] = useState(false);
  const [granting, setGranting] = useState(false);
  const [grantAmount, setGrantAmount] = useState(1000);
  const [grantReason, setGrantReason] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetTo, setResetTo] = useState(0);
  const [resetReason, setResetReason] = useState('');
  const isSuperAdmin = user?.is_super_admin || user?.user_type === 'super_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, h] = await Promise.all([
        api().get('/ai-credits/balance'),
        api().get('/ai-credits/history'),
      ]);
      setBalance(b.data.balance);
      setPackages(b.data.packages || []);
      setHistory(h.data || []);
    } catch (e) {
      toast.error('Could not load credit balance');
    }
    setLoading(false);
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe redirect
  useEffect(() => {
    if (params.get('success')) {
      toast.success('Payment received — credits will appear shortly.');
      setParams({});
      load();
    } else if (params.get('cancelled')) {
      toast.info('Top-up cancelled.');
      setParams({});
    }
  }, [params, setParams, load]);

  // Handle PayPal redirect (approval URL returns with ?paypal=success&token=ORDER_ID&PayerID=...)
  useEffect(() => {
    const pp = params.get('paypal');
    if (!pp) return;
    if (pp === 'cancelled') {
      toast.info('PayPal payment cancelled.');
      setParams({});
      return;
    }
    if (pp === 'success') {
      const orderId = params.get('token');
      if (!orderId) { setParams({}); return; }
      setCapturingPp(true);
      api().post('/ai-credits/paypal/capture', { order_id: orderId })
        .then((r) => {
          if (r.data.success) {
            toast.success(r.data.message || 'PayPal payment confirmed.');
          } else {
            toast.warning(r.data.message || 'PayPal payment is still processing.');
          }
        })
        .catch((e) => toast.error(e?.response?.data?.detail || 'PayPal capture failed'))
        .finally(() => {
          setCapturingPp(false);
          setParams({});
          load();
        });
    }
  }, [params, setParams, load, api]);

  const buy = async (pkgId) => {
    setBuying(pkgId);
    try {
      const r = await api().post('/ai-credits/topup', { package_id: pkgId });
      if (r.data.mode === 'stripe' && r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.success(r.data.message || 'Credits added');
        await load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Top-up failed');
    }
    setBuying(null);
  };

  const buyWithPaypal = async (pkgId) => {
    setPayingPp(pkgId);
    try {
      const r = await api().post('/ai-credits/paypal/create-order', { package_id: pkgId });
      if (r.data?.approval_url) {
        window.location.href = r.data.approval_url;
      } else {
        toast.error('PayPal did not return an approval URL.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not start PayPal checkout');
    }
    setPayingPp(null);
  };

  const fmt = (n) => new Intl.NumberFormat().format(n);

  return (
    <div className="space-y-8" data-testid="ai-credits-page">
      {/* Super-admin manual grant panel — visible ONLY to super admin */}
      {isSuperAdmin && (
        <div className="rounded-2xl bg-gradient-to-br from-amber-50 to-white border-2 border-[#F5A623]/30 p-6" data-testid="super-admin-grant-panel">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-bold text-[#1A2A4F]">Super Admin · Manual credit grant</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Gift credits to this tenant without payment. Use a negative number to revoke credits.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-[#1A2A4F] mb-1">Credits</label>
              <input
                type="number"
                value={grantAmount}
                onChange={(e) => setGrantAmount(parseInt(e.target.value || '0', 10))}
                placeholder="1000"
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
                data-testid="grant-amount-input"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-[#1A2A4F] mb-1">Reason (optional)</label>
              <input
                type="text"
                value={grantReason}
                onChange={(e) => setGrantReason(e.target.value)}
                placeholder="e.g. Launch bonus, contract bonus, support credit"
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#F5A623]/20 focus:border-[#F5A623] outline-none"
                data-testid="grant-reason-input"
              />
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              onClick={async () => {
                if (!grantAmount || isNaN(grantAmount)) { toast.error('Enter a credit amount'); return; }
                setGranting(true);
                try {
                  const r = await api().post('/ai-credits/grant', { credits: grantAmount, reason: grantReason });
                  toast.success(r.data.message + ` New balance: ${new Intl.NumberFormat().format(r.data.balance)}.`);
                  setGrantReason('');
                  await load();
                } catch (e) { toast.error(e?.response?.data?.detail || 'Grant failed'); }
                setGranting(false);
              }}
              disabled={granting || !grantAmount}
              className="bg-[#F5A623] text-white text-sm font-bold px-5 py-2.5 rounded-md hover:brightness-95 disabled:opacity-50 inline-flex items-center gap-2"
              data-testid="grant-credits-btn"
            >
              {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
              {grantAmount >= 0 ? 'Grant credits' : 'Revoke credits'}
            </button>
            <span className="text-xs text-muted-foreground">No payment is taken — these are free credits to your client.</span>
          </div>

          {/* Force Reset sub-panel */}
          <div className="mt-6 border-t border-amber-200/60 pt-4" data-testid="super-admin-force-reset-panel">
            <h3 className="text-sm font-bold text-[#1A2A4F] flex items-center gap-2">
              <Shield className="w-4 h-4 text-rose-600" /> Force-reset balance (override)
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Overwrites the current balance to the exact value below. The delta is logged in the audit trail.
            </p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-semibold text-[#1A2A4F] mb-1">New balance</label>
                <input
                  type="number"
                  min={0}
                  value={resetTo}
                  onChange={(e) => setResetTo(parseInt(e.target.value || '0', 10))}
                  placeholder="0"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 outline-none"
                  data-testid="reset-balance-input"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-[#1A2A4F] mb-1">Reason (recommended)</label>
                <input
                  type="text"
                  value={resetReason}
                  onChange={(e) => setResetReason(e.target.value)}
                  placeholder="e.g. End-of-trial reset, contract renewal, fraud rollback"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 outline-none"
                  data-testid="reset-reason-input"
                />
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <button
                onClick={async () => {
                  if (isNaN(resetTo) || resetTo < 0) { toast.error('Enter a non-negative balance'); return; }
                  const ok = window.confirm(`Force-reset balance from ${fmt(balance)} to ${fmt(resetTo)}? This is logged in the audit trail.`);
                  if (!ok) return;
                  setResetting(true);
                  try {
                    const r = await api().post('/ai-credits/set', { balance: resetTo, reason: resetReason });
                    toast.success(r.data.message + ` Delta: ${r.data.delta >= 0 ? '+' : ''}${r.data.delta}.`);
                    setResetReason('');
                    await load();
                  } catch (e) { toast.error(e?.response?.data?.detail || 'Reset failed'); }
                  setResetting(false);
                }}
                disabled={resetting}
                className="bg-rose-600 text-white text-sm font-bold px-5 py-2.5 rounded-md hover:brightness-95 disabled:opacity-50 inline-flex items-center gap-2"
                data-testid="reset-credits-btn"
              >
                {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <AlertCircle className="w-4 h-4" />}
                Force reset balance
              </button>
              <span className="text-xs text-muted-foreground">Overrides current balance to the absolute value above.</span>
            </div>
          </div>
        </div>
      )}

      {/* Balance hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1A2A4F] via-[#243a6a] to-[#1A2A4F] text-white p-8">
        <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-[#F5A623]/30 blur-3xl pointer-events-none" />
        <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <p className="text-[#F5A623] font-semibold tracking-widest text-xs uppercase">AI Credits balance</p>
            <h1 className="mt-2 text-5xl font-bold flex items-baseline gap-3">
              {loading ? <Loader2 className="w-8 h-8 animate-spin" /> : fmt(balance)}
              <span className="text-base font-normal opacity-70">credits</span>
            </h1>
            <p className="mt-2 text-sm opacity-80 max-w-md">
              Credits power AI Counselor chats, document analysis, course matchmaking, and the OTP email gateway.
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
            <Sparkles className="w-4 h-4 text-[#F5A623]" />
            <span className="text-sm font-semibold">1 credit ≈ 1 AI call</span>
          </div>
        </div>
      </div>

      {/* Packages */}
      <div>
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-foreground">Top up credits</h2>
            <p className="text-sm text-muted-foreground mt-1">Pay securely via Stripe or PayPal. Credits never expire.</p>
          </div>
        </div>
        {capturingPp && (
          <div className="mb-3 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-900 inline-flex items-center gap-2" data-testid="paypal-capturing">
            <Loader2 className="w-3 h-3 animate-spin" /> Confirming PayPal payment…
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ai-credit-packages">
          {packages.map((p) => {
            const isHi = p.highlight;
            return (
              <div
                key={p.id}
                className={`relative rounded-2xl border p-6 flex flex-col ${isHi ? 'border-[#F5A623] shadow-lg shadow-[#F5A623]/15 bg-amber-50/30' : 'border-border bg-card'}`}
                data-testid={`credit-pkg-${p.id}`}
              >
                {isHi && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#F5A623] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
                    Most Popular
                  </span>
                )}
                <div className="flex items-center gap-2">
                  {p.id === 'pro' ? <Crown className="w-4 h-4 text-[#F5A623]" /> :
                   p.id === 'scale' ? <TrendingUp className="w-4 h-4 text-[#F5A623]" /> :
                   <Zap className="w-4 h-4 text-[#F5A623]" />}
                  <h3 className="font-bold text-foreground">{p.label}</h3>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{p.best_for}</p>
                <p className="mt-5 text-4xl font-bold text-foreground">{fmt(p.credits)}</p>
                <p className="text-xs text-muted-foreground">AI credits</p>
                <p className="mt-3 text-2xl font-bold text-[#1A2A4F]">${p.price_usd}</p>
                <p className="text-xs text-muted-foreground">one-time</p>
                <button
                  onClick={() => buy(p.id)}
                  disabled={buying === p.id || payingPp === p.id}
                  className={`mt-5 w-full font-bold py-2.5 rounded-md transition flex items-center justify-center gap-2 ${
                    isHi ? 'bg-[#F5A623] text-white hover:brightness-95' : 'border border-[#F5A623] text-[#F5A623] hover:bg-[#F5A623]/5'
                  }`}
                  data-testid={`buy-pkg-${p.id}`}
                >
                  {buying === p.id ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : 'Pay with Card (Stripe)'}
                </button>
                <button
                  onClick={() => buyWithPaypal(p.id)}
                  disabled={buying === p.id || payingPp === p.id}
                  className="mt-2 w-full font-bold py-2.5 rounded-md transition flex items-center justify-center gap-2 bg-[#FFC439] text-[#003087] hover:brightness-95 disabled:opacity-50"
                  data-testid={`buy-pkg-paypal-${p.id}`}
                >
                  {payingPp === p.id
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening PayPal…</>
                    : <><span className="font-extrabold tracking-tight">Pay<span className="text-[#009cde]">Pal</span></span></>}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* History */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="w-4 h-4 text-muted-foreground" />
          <h2 className="text-lg font-bold text-foreground">Recent transactions</h2>
        </div>
        {history.length === 0 ? (
          <div className="bg-card border border-dashed border-border rounded-xl py-10 text-center text-sm text-muted-foreground" data-testid="credit-history-empty">
            No top-ups yet. Buy a package above to get started.
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl divide-y divide-border" data-testid="credit-history-list">
            {history.map((t) => (
              <div key={t.id} className="px-5 py-3.5 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm text-foreground">+{fmt(t.amount)} credits ({t.package_id})</p>
                  <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-foreground">${t.price_usd}</p>
                  <p className={`text-[11px] uppercase tracking-wider font-semibold ${
                    t.status === 'completed' ? 'text-emerald-600' : t.status === 'pending' ? 'text-amber-500' : 'text-red-500'
                  }`}>{t.status}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-xl bg-blue-50 border border-blue-100 p-4 flex items-start gap-3 text-sm">
        <AlertCircle className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-blue-900">
          <strong>About AI credits:</strong> AI Counsellor chats, OCR document scanning, and the course matchmaker each consume 1 credit per call. Email/SMS OTPs are free. Credits never expire — top up only when you run low.
        </p>
      </div>
    </div>
  );
};

export default AICreditsPage;
